import compose from 'recompose/compose'
import { connect } from 'react-redux'
import React from 'react'

import InteractiveElements from '../../components/InteractiveElements'
import { delay } from '../../../utils/functions'
import { from } from 'rxjs/observable/from'
import { INTERACTIVE_ELEMENT_OPTIONS } from '../../constants'
import { getCurrentPoint } from 'aria-viewer/utils/functions'
import idx from 'idx'
import debounce from 'lodash-es/debounce'
import { intiTHREEMusketeers, disposeTHREEMusketeerHitMeshes } from 'aria-viewer/players/three'
import { getEnv } from 'aria-viewer/utils/environment'
import { redrawPanoramaScene } from 'aria-viewer/players/panoplayer/actions/panoramaActions'

const mapStateToProps = state => {
    const isMobile = state.getIn(['device', 'isDeviceMobile'])
    const hydrated = state.getIn(['interactiveElements', 'hydrated'])
    const stopId = state.getIn(['interactiveElements', 'currentScene', 'stopId'])
    const isMainMedia = idx(getCurrentPoint(state.get('tour')), _ => _.main)

    return {
        state,
        hydrated,
        stopId,
        isMobile,
        isMainMedia,
    }
}

const mapDispatchToProps = dispatch => ({
    onMount: () => {
        dispatch(redrawPanoramaScene())
    },
})

// At the moment interactive elements should only work when on the mainMedia
const enableInteractiveElements = Component => ({
    enableInteractiveElements,
    renderer,
    contextType,
    isMainMedia,
    ...props
}) =>
    enableInteractiveElements &&
    isMainMedia &&
    (contextType == INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D || renderer) ? (
        <Component {...props} contextType={contextType} renderer={renderer} />
    ) : null

const stopProps = function(Component) {
    return class extends React.Component {
        shouldComponentUpdate({ hydrated, stopId }) {
            if (this.init) {
                return (!!hydrated && !this.props.hydrated) || stopId !== this.props.stopId
            } else {
                this.init = true
                return true
            }
        }
        render() {
            return <Component {...this.props} />
        }
    }
}

const getElementsFromState = Component => ({ state, contextType, ...props }) => {
    let hotspots = [],
        triggers = [],
        positionalMedia = [],
        stopId = null
    const mainPano = state.getIn(['interactiveElements', 'currentScene', 'scene'])
    if (mainPano) {
        const hs = mainPano.get('hotspots')
        const tr = mainPano.get('triggers')
        const pm = mainPano.get('positionalMedia')
        hotspots = hs ? hs.toJS() : []
        triggers = tr ? tr.toJS() : []
        positionalMedia = pm ? pm.toJS() : []
        stopId = state.getIn(['interactiveElements', 'currentScene', 'stopId'])

        // If no interactive elements are present then no need to render IE container.
        return contextType == INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D &&
            (hotspots.length < 1 && triggers.length < 1 && positionalMedia.length < 1) ? null : (
            <Component {...{ ...props, hotspots, triggers, stopId, positionalMedia, contextType }} />
        )
    }
    return null
}

const delayMount = function(Component) {
    return class extends React.PureComponent {
        constructor(props) {
            super(props)
            this.state = {
                ready: false,
            }
        }

        delayMount = () => {
            this.delay = from(delay(750)).subscribe(() => requestAnimationFrame(() => this.setState({ ready: true })))
        }

        componentDidMount() {
            this.delayMount()
        }

        componentWillUpdate({ stopId }) {
            if (stopId !== this.props.stopId) {
                this.delay && this.delay.unsubscribe()
                this.setState({ ready: false })

                this.delayMount()
            }
        }

        render() {
            return this.state.ready && <Component {...this.props} />
        }
    }
}

// THREEMusketeers - tool to introspect, debug and test any THREE.js application.
// Only for use on non-production environment.
const WithTHREEMusketeers = Component => {
    return class extends React.PureComponent {
        constructor(props) {
            super(props)

            // Wait until scene has completely loaded
            this.handleOnInteractiveElementsMounted = debounce(this.handleOnInteractiveElementsMounted, 500)
        }

        handleOnInteractiveElementsMounted = async () => {
            const { onMount, contextType } = this.props
            onMount && onMount()

            if (getEnv('NODE_ENV') !== 'production' && contextType !== INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D) {
                const { scene, camera, renderer } = this.props

                this.hitmeshes = await intiTHREEMusketeers({ scene, camera, renderer })
            }
        }

        componentDidMount() {
            this.handleOnInteractiveElementsMounted()
        }

        componentWillUnmount() {
            this.props.onUnmount && this.props.onUnmount()

            this.hitmeshes && disposeTHREEMusketeerHitMeshes(this.hitmeshes)
        }

        render() {
            return <Component {...this.props} />
        }
    }
}

const With2DContextGetHitMeshes = Component => {
    return class extends React.PureComponent {
        constructor(props) {
            super(props)

            // Wait until scene has completely loaded
            this.handleOnInteractiveElementsMounted = debounce(this.handleOnInteractiveElementsMounted, 500)
        }

        handleOnInteractiveElementsMounted = async () => {
            const { contextType } = this.props

            if (getEnv('NODE_ENV') !== 'production' && contextType != INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context3D) {
                const hotspots = $('div[data-test="Hotspot"]')
                const triggers = $('div[data-test="Trigger"] [class*="preview"]')
                const mediaButtons = $('div[data-test="MediaButton"]')

                window.get2DHitMeshes = () => {
                    return [...hotspots, ...triggers, ...mediaButtons]
                }
            }
        }

        componentDidMount() {
            this.handleOnInteractiveElementsMounted()
        }

        render() {
            return <Component {...this.props} />
        }
    }
}

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    enableInteractiveElements,
    stopProps,
    getElementsFromState,
    delayMount,
    WithTHREEMusketeers,
    With2DContextGetHitMeshes
)(InteractiveElements)
